import { Plane, Train, Bus, Hotel, Car, Bike, Package, FileText, Truck, KeyRound, ArrowLeftRight, Palmtree } from "lucide-react";

// Canonical travel-type set — shared by the customer request form AND the agent listing form
// so the two always line up. `meta` = fields the agent fills on a non-mobility listing;
// `fields` = what the customer fills when requesting. Mobility types (`mobility: true`) use the
// dedicated mobility config block instead of dated departures.
export const TRAVEL_TYPES = [
  // ---- Air, Rail & Stay (unchanged behaviour) ----
  { key: "flight", label: "Flight", icon: Plane, group: "Air, Rail & Stay", pax: true,
    meta: [["from", "From"], ["to", "To"], ["class", "Class"]],
    fields: [["from", "From", "text"], ["to", "To", "text"], ["depart", "Depart date", "date"], ["return", "Return date (optional)", "date"], ["class", "Class", "text"]] },
  { key: "train", label: "Train", icon: Train, group: "Air, Rail & Stay", pax: true,
    meta: [["from", "From"], ["to", "To"], ["class", "Class"]],
    fields: [["from", "From", "text"], ["to", "To", "text"], ["depart", "Depart date", "date"], ["return", "Return date (optional)", "date"], ["class", "Class", "text"]] },
  { key: "bus", label: "Bus (scheduled tickets)", icon: Bus, group: "Air, Rail & Stay", pax: true,
    meta: [["from", "From"], ["to", "To"], ["class", "Type"]],
    fields: [["from", "From", "text"], ["to", "To", "text"], ["depart", "Depart date", "date"], ["return", "Return date (optional)", "date"], ["class", "Class / type", "text"]] },
  { key: "hotel", label: "Hotel", icon: Hotel, group: "Air, Rail & Stay", pax: true, paxLabel: "guests",
    meta: [["city", "City / area"]],
    fields: [["city", "City / area", "text"], ["checkin", "Check-in", "date"], ["checkout", "Check-out", "date"], ["rooms", "Rooms", "number"], ["class", "Category", "text"]] },

  // ---- Passenger Mobility ----
  mob("cab", "Cab / Taxi", Car, "detailed", "e.g. Guwahati Airport to City Cab Service",
    ["Point-to-Point", "Local", "Airport Transfer", "Railway Transfer", "Outstation", "Intercity", "Hourly Rental", "Full-Day Rental", "Corporate", "Sightseeing", "Scheduled", "Private"]),
  mob("bike", "Bike Ride", Bike, "detailed", "e.g. Local Bike Ride Service",
    ["Point-to-Point", "Local", "Scheduled", "Outstation", "Private", "Other"]),
  mob("auto", "Auto / Tuk-Tuk", Car, "detailed", "e.g. Guwahati Local Auto Service",
    ["Point-to-Point", "Local", "Scheduled", "Private", "Outstation", "Hourly", "Full-Day"]),
  mob("erickshaw", "E-Rickshaw", Bike, "detailed", "e.g. Local E-Rickshaw Passenger Service",
    ["Local Passenger", "Point-to-Point", "Scheduled", "Private", "Local Delivery", "Cargo", "Other"]),
  mob("car_rental", "Car Rental", Car, "rental", "e.g. Self-Drive / Chauffeur Car Rental", null),
  mob("bike_rental", "Bike Rental", Bike, "rental", "e.g. Scooter & Bike Rental", null),
  mob("van", "Van", Car, "rental", "e.g. Van Hire for Groups", null),
  mob("suv", "SUV", Car, "rental", "e.g. SUV Hire (Innova / Ertiga)", null),
  mob("tempo", "Tempo Traveller", Bus, "rental", "e.g. 12-Seater Tempo Traveller Hire", null),
  mob("bus_charter", "Bus (charter / rental)", Bus, "rental", "e.g. Charter Bus for Events & Tours", null),
  mob("chauffeur", "Chauffeur Service", Car, "transfer", "e.g. Professional Chauffeur Service", null),
  mob("airport_transfer", "Airport Transfer", Car, "transfer", "e.g. Airport Pickup & Drop", null),
  mob("railway_transfer", "Railway Station Transfer", Car, "transfer", "e.g. Railway Station Pickup & Drop", null),
  mob("outstation", "Outstation / Intercity", Car, "transfer", "e.g. Guwahati to Shillong Outstation Cab", null),
  mob("sightseeing", "Tourist / Sightseeing", Car, "transfer", "e.g. Full-Day Sightseeing Cab", null),
  mob("other_passenger", "Other Passenger Transport", Car, "transfer", "e.g. Passenger Transport Service", null),

  // ---- Goods / Moving ----
  mob("parcel", "Parcel Delivery", Package, "goods", "e.g. Same-Day Parcel Delivery", null),
  mob("local_delivery", "Local Delivery", Package, "goods", "e.g. Local Delivery Service", null),
  mob("goods_transport", "Goods Transport", Truck, "goods", "e.g. City Goods Transport", null),
  mob("mini_truck", "Pickup / Mini Truck", Truck, "goods", "e.g. Mini Truck (Tata Ace) Hire", null),
  mob("truck", "Truck Transport", Truck, "goods", "e.g. Full-Load Truck Transport", null),
  mob("movers", "Movers & Packers", Truck, "goods", "e.g. Home Shifting & Packing Service", null),

  // ---- Holidays & Utilities (unchanged behaviour) ----
  { key: "package", label: "Holiday Packages", icon: Package, group: "Holidays & Utilities", pax: true,
    meta: [["destination", "Destination"]],
    fields: [["destination", "Destination", "text"], ["dates", "Dates", "text"], ["budget", "Budget (optional)", "text"]] },
  { key: "utility", label: "Visa / Travel Utilities", icon: FileText, group: "Holidays & Utilities", applicants: true,
    meta: [["service_type", "Service type"], ["country", "Country / authority"]],
    fields: [["service_type", "Service type (Visa/Passport/…)", "text"], ["country", "Country / authority", "text"]] },
];

// shared service-type presets for the non-detailed mobility buckets

export const travelTypeMap = Object.fromEntries(TRAVEL_TYPES.map((t) => [t.key, t]));

// Passenger / occupancy counters shown on the per-type search form (in addition to the
// route/date/class fields). Bus uses a single "Passengers" count; it maps to `adults` in the
// quote payload. Types not listed here show no counters on their search form.
export const PAX_FIELDS = {
  flight: [["adults", "Adults"], ["children", "Children"], ["infants", "Infants"]],
  train: [["adults", "Adults"], ["children", "Children"]],
  bus: [["passengers", "Passengers"]],
  hotel: [["adults", "Adults"], ["children", "Children"]],
  package: [["adults", "Adults"], ["children", "Children"]],
};

// Preloaded class / category options for the search + quote forms (rendered as a dropdown on the
// "class" field). Flight cabins, Indian Railways classes, bus service types, hotel star tiers.
export const CLASS_OPTIONS = {
  flight: ["Economy", "Premium Economy", "Business", "First"],
  train: ["Sleeper (SL)", "AC 3 Tier (3A)", "AC 2 Tier (2A)", "AC First (1A)", "Chair Car (CC)", "Exec Chair (EC)", "Second Sitting (2S)", "General (GN)"],
  bus: ["AC Seater", "AC Sleeper", "Non-AC Seater", "Non-AC Sleeper", "Semi-Sleeper", "Volvo A/C"],
  hotel: ["3★", "4★", "5★"],
};

// Normal-search passenger cap per type (adults + children + infants). Over the cap, the traveller
// switches to a Group enquiry. Flight 9, Train/Bus 6 per ticket.
export const PAX_CAP = { flight: 9, train: 6, bus: 6 };
export const paxCapFor = (key) => PAX_CAP[key] || 9;

export const MOBILITY_KEYS = TRAVEL_TYPES.filter((t) => t.mobility).map((t) => t.key);
export const isMobilityType = (key) => !!travelTypeMap[key]?.mobility;
export const PASSENGER_BUCKETS = ["detailed", "rental", "transfer"];

export const MOBILITY_PRICING = [
  ["fixed", "Fixed Fare"],
  ["per_km", "Per KM"],
  ["per_hour", "Per Hour"],
  ["per_km_hour", "Per KM + Per Hour"],
  ["per_trip", "Per Trip"],
  ["per_passenger", "Per Passenger"],
  ["quote", "Quote Required"],
];
export const GOODS_PRICING = [
  ["fixed", "Fixed Fare"],
  ["per_trip", "Per Trip"],
  ["per_km", "Per KM"],
  ["quote", "Quote Required"],
];
export const RIDE_TYPES = [["instant", "Instant"], ["scheduled", "Scheduled"], ["both", "Both"]];
export const TRIP_TYPES = [
  ["one_way", "One Way"], ["round_trip", "Round Trip"], ["hourly", "Hourly"], ["full_day", "Full Day"],
  ["multi_day", "Multi-Day"], ["outstation", "Outstation"], ["intercity", "Intercity"],
];

export const pricingOptionsFor = (key) => {
  const t = travelTypeMap[key];
  if (!t?.mobility) return MOBILITY_PRICING;
  return t.bucket === "goods" ? GOODS_PRICING : MOBILITY_PRICING;
};
export const serviceTypeOptionsFor = (key) => travelTypeMap[key]?.serviceTypes || [];
export const supportsWaiting = (key) => {
  const t = travelTypeMap[key];
  return !!(t?.mobility && PASSENGER_BUCKETS.includes(t.bucket));
};
export const mobilityNamePlaceholder = (key) => travelTypeMap[key]?.placeholder || "e.g. My travel service";

const money = (n) => `₹${Number(n) || 0}`;
export const mobilityPriceLine = (mob) => {
  if (!mob) return "";
  const pm = mob.pricing_model || "quote";
  if (pm === "quote") return "Request Quote";
  if (pm === "fixed") return `${money(mob.base_fare)} fixed fare`;
  if (pm === "per_trip") return `${money(mob.trip_fare)} per trip`;
  if (pm === "per_km") return `${money(mob.rate_km)}/km · Final fare based on actual KM${mob.min_km ? ` · Min ${mob.min_km} km` : ""}`;
  if (pm === "per_hour") return `${money(mob.rate_hour)}/hour${mob.min_hours ? ` · Minimum ${mob.min_hours} hours` : ""}`;
  if (pm === "per_km_hour") return `${money(mob.rate_km)}/km + ${money(mob.rate_hour)}/hour`;
  if (pm === "per_passenger") return `${money(mob.fare_a)}/person`;
  return "";
};
export const mobilityWaitingLine = (mob) => {
  const w = mob?.waiting;
  if (!w || !w.enabled || !(Number(w.rate) > 0)) return "";
  const unit = w.rate_unit === "hr" ? "hr" : "min";
  return `Waiting ${money(w.rate)}/${unit}${Number(w.free_minutes) > 0 ? ` · ${w.free_minutes} min free` : ""}`;
};
// A mobility listing is directly bookable only for concrete amounts (Fixed / Per Trip);
// everything else routes to Request Quote (final fare confirmed by the provider).
export const mobilityIsBookable = (mob) => ["fixed", "per_trip"].includes(mob?.pricing_model);

// Which travel fields draw from the verified Travel Location Master (returns the master mode
// key), and which stay free text (returns null). Mobility & package = free text.
export const locationModeFor = (travelTypeKey, fieldKey) => {
  if ((travelTypeKey === "flight" || travelTypeKey === "train" || travelTypeKey === "bus") && (fieldKey === "from" || fieldKey === "to")) return travelTypeKey;
  if (travelTypeKey === "hotel" && fieldKey === "city") return "hotel";
  return null;
};

// helper builder for a mobility travel type
function mob(key, label, icon, bucket, placeholder, serviceTypes) {
  const group = bucket === "goods" ? "Goods / Moving" : "Passenger Mobility";
  const presets = {
    rental: ["Hourly Rental", "Full-Day Rental", "Multi-Day", "Outstation", "Local", "Corporate", "Sightseeing", "Airport Transfer", "Other"],
    transfer: ["Airport Transfer", "Railway Transfer", "Point-to-Point", "Outstation", "Intercity", "Sightseeing", "Local", "Private", "Other"],
    goods: ["Local Delivery", "Intercity", "Door-to-Door", "Same-Day", "Scheduled", "Bulk / Full Load", "Part Load", "Other"],
  };
  const st = Array.isArray(serviceTypes) ? serviceTypes : (presets[bucket] || []);
  return {
    key, label, icon, group, mobility: true, bucket, placeholder,
    pax: bucket !== "goods", paxLabel: "passengers", serviceTypes: st,
    meta: [], fields: [["pickup", "Pickup", "text"], ["drop", "Drop", "text"], ["datetime", "Date & time", "datetime-local"]],
  };
}

// Grouped options for a <select> with <optgroup>s.
export const TRAVEL_TYPE_GROUPS = (() => {
  const order = ["Air, Rail & Stay", "Passenger Mobility", "Goods / Moving", "Holidays & Utilities"];
  const byGroup = {};
  for (const t of TRAVEL_TYPES) (byGroup[t.group] = byGroup[t.group] || []).push(t);
  return order.filter((g) => byGroup[g]).map((g) => ({ group: g, items: byGroup[g] }));
})();

// ---- Customer-facing high-level categories (10). Each maps to one or more travel types.
// The category row shows first; detailed types appear only after a category is picked.
export const TRAVEL_CATEGORIES = [
  { key: "flight", label: "Flights", icon: Plane, types: ["flight"] },
  { key: "train", label: "Trains", icon: Train, types: ["train"] },
  { key: "bus", label: "Bus", icon: Bus, types: ["bus"] },
  { key: "hotel", label: "Hotels", icon: Hotel, types: ["hotel"] },
  { key: "rides", label: "Rides", icon: Car, types: ["cab", "bike", "auto", "erickshaw", "other_passenger"] },
  { key: "rentals", label: "Rentals", icon: KeyRound, types: ["car_rental", "bike_rental", "van", "suv", "tempo", "bus_charter"] },
  { key: "transfers", label: "Transfers", icon: ArrowLeftRight, types: ["airport_transfer", "railway_transfer", "chauffeur"] },
  { key: "tours", label: "Tours & Holidays", icon: Palmtree, types: ["package", "sightseeing", "outstation"] },
  { key: "logistics", label: "Logistics", icon: Package, types: ["parcel", "local_delivery", "goods_transport", "mini_truck", "truck", "movers"] },
  { key: "travel_services", label: "Travel Services", icon: FileText, types: ["utility"] },
];
export const categoryForType = (typeKey) => TRAVEL_CATEGORIES.find((c) => c.types.includes(typeKey));

// Short intro copy + hero image per category — gives each drill-down a curated hero.
export const CATEGORY_COPY = {
  flight: { intro: "Compare posted airfares and lock in seats, or ask verified agents for a custom quote.", img: "https://images.unsplash.com/photo-1569242987891-d7a718c7aff6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  train: { intro: "Find train fares from trusted agents, or request a rate for the route and class you need.", img: "https://images.unsplash.com/photo-1637995735729-c43250f1ef47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  bus: { intro: "Book scheduled bus tickets or charter a coach for your group.", img: "https://images.unsplash.com/photo-1776521908392-a68ada9bb47c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  hotel: { intro: "Handpicked stays across India — book a posted rate or get a tailored quote.", img: "https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  rides: { intro: "Cabs, bikes, autos and more — book a set fare or request a per-KM quote from providers near you.", img: "https://images.unsplash.com/photo-1630717285906-29364ffacea0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  rentals: { intro: "Hire a car, bike, SUV, tempo or coach — by the hour, day or trip.", img: "https://images.unsplash.com/photo-1533558701576-23c65e0272fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  transfers: { intro: "Airport & railway pickups and chauffeur drives — reliable point-to-point transfers.", img: "https://images.unsplash.com/photo-1763618769707-3e318c25325f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  tours: { intro: "Curated holiday packages and sightseeing trips, plus outstation rides for longer journeys.", img: "https://images.unsplash.com/photo-1616388969587-8196f32388b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  logistics: { intro: "Parcels, goods, mini-trucks, trucks and full home shifting — moved by verified providers.", img: "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
  travel_services: { intro: "Visa help and travel utilities handled by experienced agents.", img: "https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0" },
};
// short labels for the detailed sub-type chips inside a category
export const SUBTYPE_LABEL = {
  cab: "Cab / Taxi", bike: "Bike Ride", auto: "Auto / Tuk-Tuk", erickshaw: "E-Rickshaw", other_passenger: "Other",
  car_rental: "Car", bike_rental: "Bike", van: "Van", suv: "SUV", tempo: "Tempo Traveller", bus_charter: "Bus / Coach",
  airport_transfer: "Airport", railway_transfer: "Railway", chauffeur: "Chauffeur",
  package: "Holiday Packages", sightseeing: "Sightseeing", outstation: "Outstation / Intercity",
  parcel: "Parcel Delivery", local_delivery: "Local Delivery", goods_transport: "Goods Transport",
  mini_truck: "Pickup / Mini Truck", truck: "Truck", movers: "Movers & Packers", utility: "Visa / Utilities",
};
